import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	exportFile,
	FormattedText,
	If,
	isAfter,
	Modal,
	Row,
	StyleProps,
	toGrammaticalNumber,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import {
	CalendarEvent,
	CalendarEventCalendarDetailQuery,
	CalendarEventUserListQuery,
	MediaAttachment,
	SharingPolicy,
	truncateDisplayItems,
	useCalendarEventUserListQuery,
} from "../../../../Utilities";
import { CalendarEventFormat, CalendarEventResponseFormat, UserFormat } from "../../../Format";
import { CalendarEventUtilities, MediaAttachmentUtilities } from "../../../Utilities";
import * as ModelComponents from "../../_General";
import * as MediaAttachmentComponents from "../../MediaAttachment";
import * as UserComponents from "../../User";
import { PublicBadge } from "../General";
import { CalendarEventUpdatePermissionRequired } from "../PermissionRequired";
import { RSVPForm } from "../RSVPForm";
import { CalendarEventStatusBadge } from "../StatusBadge";
import { ControlledCalendarEventUpdateModalController } from "../UpdateModal";
import styles from "./styles.module.css";

export type DetailDisplayCalendarEvent = Omit<
	CalendarEventCalendarDetailQuery["calendarEventConnection"]["edges"][0]["node"],
	"mediaAttachments"
> & {
	mediaAttachments: Pick<
		MediaAttachment,
		"id" | "title" | "isFile" | "fileURL" | "fileKey" | "fileContentType" | "linkURL"
	>[];
};

export type CalendarEventDetailDisplayProps = {
	calendarEvent: DetailDisplayCalendarEvent;
	withStatus?: boolean;
	editable?: boolean;
	withICalDownload?: boolean;
	isPreview?: boolean;
} & (
	| {
			withRSVPForm?: false;
			rsvpSyncID?: undefined;
			synchronizeRSVP?: undefined;
	  }
	| {
			withRSVPForm: true;
			rsvpSyncID: any;
			synchronizeRSVP: () => void;
	  }
) &
	Partial<ClassNameProps & StyleProps>;

export function CalendarEventDetailDisplay({
	calendarEvent,
	withStatus = false,
	editable = false,
	withICalDownload = false,
	isPreview = false,
	className,
	style,
	...rsvpProps
}: CalendarEventDetailDisplayProps) {
	const viewerResponse = calendarEvent.calendarEventResponses.find((e) => e.author?.isCurrent) ?? null;

	const isPastEvent = !!calendarEvent && isAfter(new Date(), CalendarEventUtilities.endTimeDate(calendarEvent), "day");

	const { visualAttachments, fileAttachments, linkAttachments } = React.useMemo(
		() =>
			MediaAttachmentUtilities.splitAndExtractMediaAttachments(
				calendarEvent.mediaAttachments,
				calendarEvent.description,
			),
		[calendarEvent.mediaAttachments, calendarEvent.description],
	);

	const { isMediaAttachmentModalOpen, onClickMediaAttachment, closeMediaAttachmentModal, mediaAttachmentModalIndex } =
		MediaAttachmentComponents.MediaAttachmentModal.useModal();

	const onDownloadClick = React.useCallback(async () => {
		if (!calendarEvent) {
			return;
		}

		let file: File | undefined;
		try {
			file = await CalendarEventUtilities.toICal(calendarEvent);
		} catch (error) {
			return;
		}
		exportFile({
			name: `${calendarEvent.name.replace(/\s+/g, "-")}.ics`,
			data: file,
		});
	}, [calendarEvent]);

	return (
		<Column justify="spaced-start" verticalSpacing="1.125rem" className={className} style={style}>
			<If condition={calendarEvent.imageURL !== ""}>
				<ModelComponents.ImageWithModal
					src={calendarEvent.imageURL}
					alt={calendarEvent.name}
					sizing="cover"
					modalBackButtonText="Back to Event"
					className={styles["detail-display__image"]}
					containerClassName={styles["detail-display__image__container"]}
				/>
			</If>

			<Column justify="spaced-start" verticalSpacing="0.375rem">
				<Row
					justify="spaced-start"
					align="center"
					className={styles["detail-display__detail--main"] + " " + styles["detail-display__detail--name"]}
				>
					<Icon.CalendarBlank /> {calendarEvent.name}
					<If condition={editable && !isPreview}>
						<CalendarEventUpdatePermissionRequired isAuthor={calendarEvent.author?.isCurrent ?? false}>
							<ControlledCalendarEventUpdateModalController
								calendarEventID={calendarEvent.id}
								variant="link"
								size="small"
							>
								Edit
							</ControlledCalendarEventUpdateModalController>
						</CalendarEventUpdatePermissionRequired>
					</If>
				</Row>
				<If condition={calendarEvent.description !== ""}>
					<div className={styles["detail-display__detail__details"]}>
						<ModelComponents.ExpandableText lines={8}>{calendarEvent.description}</ModelComponents.ExpandableText>
					</div>
				</If>
			</Column>

			<Column justify="spaced-start" verticalSpacing="0.375rem">
				<Row justify="spaced-start" align="center" className={styles["detail-display__detail--main"]}>
					<Icon.Clock /> {CalendarEventFormat.timeRange(calendarEvent)}
				</Row>
				<If condition={withStatus}>
					<div className={styles["detail-display__detail__details"]}>
						<CalendarEventStatusBadge calendarEvent={calendarEvent} style={{ width: "fit-content" }} />
					</div>
				</If>
			</Column>

			<If condition={calendarEvent.location !== ""}>
				<Row justify="spaced-start" align="center" className={styles["detail-display__detail--main"]}>
					<Icon.MapPin /> <FormattedText>{calendarEvent.location}</FormattedText>
				</Row>
			</If>

			<If condition={!isPreview}>
				<CalendarEventModalAttendeesSection calendarEvent={calendarEvent} />
			</If>

			{rsvpProps.withRSVPForm && (
				<If condition={calendarEvent.allowsResponses && !isPastEvent}>
					<Column justify="spaced-start" verticalSpacing="0.375rem">
						<Row justify="spaced-start" align="center" className={styles["detail-display__detail--main"]}>
							<Icon.EnvelopeSimple /> RSVP
						</Row>
						<div
							className={
								styles["detail-display__detail__details"] + " " + styles["detail-display__detail__details--rsvp-form"]
							}
						>
							<RSVPForm
								key={rsvpProps.rsvpSyncID}
								calendarEventID={calendarEvent.id}
								calendarEventResponse={viewerResponse}
								onSuccess={rsvpProps.synchronizeRSVP}
							/>
						</div>
					</Column>
				</If>
			)}

			<If condition={withICalDownload}>
				<div>
					<Button variant="link" onClick={onDownloadClick}>
						<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
							<Icon.CalendarPlus size={16} />
							Add to Calendar
						</Row>
					</Button>
				</div>
			</If>

			<If condition={visualAttachments.length > 0 || fileAttachments.length > 0 || linkAttachments.length > 0}>
				<Column justify="spaced-start">
					<Row justify="spaced-start" align="center" className={styles["detail-display__detail--main"]}>
						<Icon.File /> Attachments
					</Row>
					<Column justify="spaced-start" className={styles["detail-display__detail__details"]}>
						<If condition={visualAttachments.length > 0}>
							<MediaAttachmentComponents.VisualAttachmentsRow
								attachments={visualAttachments}
								onClickAttachment={onClickMediaAttachment}
							/>

							<Modal.If condition={isMediaAttachmentModalOpen}>
								<MediaAttachmentComponents.MediaAttachmentModal
									mediaAttachments={visualAttachments}
									initialIndex={mediaAttachmentModalIndex}
									backButtonText="Back to Event"
									onClose={closeMediaAttachmentModal}
								/>
							</Modal.If>
						</If>
						<If condition={fileAttachments.length > 0}>
							<MediaAttachmentComponents.FileAttachmentsRow attachments={fileAttachments} />
						</If>
						<If condition={linkAttachments.length > 0}>
							<MediaAttachmentComponents.LinkAttachmentsRow attachments={linkAttachments} />
						</If>
					</Column>
				</Column>
			</If>
		</Column>
	);
}

type CalendarEventModalAttendeesSectionProps = {
	calendarEvent: Pick<CalendarEvent, "id"> & {
		sharingPolicy: Pick<SharingPolicy, "id" | "isPublic"> | null;
	};
};

function CalendarEventModalAttendeesSection({ calendarEvent }: CalendarEventModalAttendeesSectionProps) {
	const { data } = useCalendarEventUserListQuery({
		variables: {
			calendarEventID: calendarEvent.id,
			pagination: { first: 10 },
		},
	});
	const totalUsers = data?.userConnection.totalCount ?? 0;
	const users = React.useMemo(() => data?.userConnection.edges.map((edge) => edge?.node) ?? [], [data]);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();
	const numAdditional = totalUsers - users.length;
	const { displayedItems, anyHidden, numHidden } = truncateDisplayItems(users, 5, { numAdditional });

	if (totalUsers > 0) {
		return (
			<Column justify="spaced-start" verticalSpacing="0.375rem">
				<Row justify="spaced-start" align="center" className={styles["detail-display__detail--main"]}>
					<Icon.Users /> {totalUsers} Invited
					<If condition={calendarEvent.sharingPolicy?.isPublic ?? false}>
						<PublicBadge size="small" />
					</If>
				</Row>

				<div className={styles["detail-display__detail__details"]}>
					<Column justify="spaced-start" verticalSpacing="0.5rem">
						<Column justify="spaced-start" verticalSpacing="0.5rem">
							{displayedItems.map((user) => {
								const response =
									user.authoredCalendarEventResponses.length > 0 ? user.authoredCalendarEventResponses[0] : null;
								return (
									<Row key={user.id} justify="spaced-start" align="center" horizontalSpacing="0.5rem">
										<UserComponents.UserIcon user={user} size={20} />
										{UserFormat.name(user)}
										{!!response && (
											<span className={styles["detail-display__attendee__status"]}>
												{CalendarEventResponseFormat.Fields.response(response.response)}
											</span>
										)}
									</Row>
								);
							})}
							<If condition={anyHidden || numAdditional > 0}>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
									<UserComponents.UserAdditionalCountIcon count={numHidden + numAdditional} size={20} />
									{toGrammaticalNumber("Other", numHidden + numAdditional, true)}
								</Row>
							</If>
						</Column>

						<If condition={anyHidden}>
							<Button variant="link" size="small" onClick={openModal}>
								Show All
							</Button>
						</If>

						<Modal.If condition={isModalOpen}>
							<ControlledUsersListModal calendarEventID={calendarEvent.id} onClose={closeModal} />
						</Modal.If>
					</Column>
				</div>
			</Column>
		);
	}

	if (calendarEvent.sharingPolicy?.isPublic) {
		return <PublicBadge size="medium" style={{ width: "fit-content" }} />;
	}

	return null;
}

type ControlledUsersListModalProps = Pick<
	UserComponents.UsersListModalProps<CalendarEventUserListQuery["userConnection"]["edges"][0]["node"]>,
	"onClose"
> & {
	calendarEventID: CalendarEvent["id"];
};

function ControlledUsersListModal({ calendarEventID, ...props }: ControlledUsersListModalProps) {
	const pageSize = 50;

	const { loading, data, fetchMore } = useCalendarEventUserListQuery({
		variables: {
			calendarEventID,
			pagination: { first: pageSize },
		},
	});
	const endCursor = data?.userConnection.pageInfo.endCursor;
	const loadMore = React.useCallback(() => {
		fetchMore({
			variables: { pagination: { first: pageSize, after: endCursor } },
			updateQuery: (prev, { fetchMoreResult }) => ({
				...prev,
				userConnection: {
					...prev.userConnection,
					pageInfo: fetchMoreResult.userConnection.pageInfo,
					edges: [...prev.userConnection.edges, ...fetchMoreResult.userConnection.edges],
				},
			}),
		});
	}, [fetchMore, pageSize, endCursor]);
	const users = React.useMemo(() => data?.userConnection.edges.map((e) => e.node), [data]);

	return (
		<UserComponents.UsersListModal
			backButtonText="Back to Event"
			heading="Event Attendees"
			isLoading={loading}
			users={users}
			getUserDescription={getUserDescription}
			hasMore={data?.userConnection.pageInfo.hasNextPage}
			loadMore={loadMore}
			className={styles["detail-display__attendees_modal"]}
			{...props}
		/>
	);
}

function getUserDescription(user: CalendarEventUserListQuery["userConnection"]["edges"][0]["node"]) {
	const response = user.authoredCalendarEventResponses.length > 0 ? user.authoredCalendarEventResponses[0] : null;
	return (
		<span className={styles["detail-display__attendee__status"]}>
			{response ? CalendarEventResponseFormat.Fields.response(response.response) : "Invited"}
		</span>
	);
}
